import type { Component } from '../router';
import { listShopProducts, type Product } from '../api/client';
import {
  ensureMotifDefs,
  frondField,
  formOf,
  formLabel,
  imagesOf,
  productPhoto,
} from '../components/shop/frondArt';
import { fdaDisclaimer, withClaimMarker } from '../components/shop/disclaimer';

// formatPrice renders minor-unit prices (cents) as a localized currency
// string. Exported for unit testing.
export function formatPrice(priceCents: number, currency: string): string {
  const amount = priceCents / 100;
  if (!currency) return amount.toFixed(2);
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
  } catch {
    // Unknown currency code — fall back to a plain amount + code.
    return `${amount.toFixed(2)} ${currency}`;
  }
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

// ProductsPage lists the public catalog from GET /api/shop/products. Public —
// no auth required. Built under the one-motif direction: each product sits in a
// leaf field as a drawn bottle (OI-002). Loading, empty, and error states are
// handled in place.
export class ProductsPage implements Component {
  private root: HTMLElement | null = null;
  private controller: AbortController | null = null;

  mount(host: HTMLElement): void {
    ensureMotifDefs();
    const section = el('section', 'shop catalog');

    const intro = el('div', 'catalog__intro');
    intro.append(
      el('h1', 'catalog__title', 'Two ways to breathe easier.'),
      el(
        'p',
        'catalog__lede',
        'The same bioactive polyphenols from the fern Dicksonia sellowiana. Choose the daily liquid, or tablets for grab-and-go.',
      ),
    );

    const grid = el('div', 'catalog__grid');
    grid.append(el('p', 'shop__status', 'Loading products…'));
    // One FDA disclaimer for the catalog — it carries every product's claims
    // (Brand §7). The per-card claims carry the matching asterisk marker.
    section.append(intro, grid, fdaDisclaimer());

    host.appendChild(section);
    this.root = section;
    void this.load(grid);
  }

  unmount(): void {
    this.controller?.abort();
    this.controller = null;
    this.root?.remove();
    this.root = null;
  }

  private async load(grid: HTMLElement): Promise<void> {
    this.controller = new AbortController();
    try {
      const res = await listShopProducts({}, this.controller.signal);
      grid.innerHTML = '';
      const products = res.products ?? [];
      if (products.length === 0) {
        grid.appendChild(el('p', 'shop__status', 'No products are available right now.'));
        return;
      }
      for (const p of products) {
        grid.appendChild(this.card(p));
      }
    } catch (err) {
      if ((err as { name?: string })?.name === 'AbortError') return;
      grid.innerHTML = '';
      grid.appendChild(
        el('p', 'shop__status shop__status--error', 'Could not load products. Please try again.'),
      );
    }
  }

  private card(p: Product): HTMLElement {
    const card = el('a', 'product-card') as HTMLAnchorElement;
    card.href = `/products/${encodeURIComponent(p.slug)}`;

    // Real photo when the product carries one (first image is the card face);
    // otherwise the drawn frond art stands in (OI-002).
    const media = el('div', 'product-card__media');
    const photos = imagesOf(p);
    if (photos.length > 0) {
      media.classList.add('product-card__media--photo');
      media.appendChild(productPhoto(photos[0], p.name, 'product-photo'));
    } else {
      media.appendChild(frondField(formOf(p)));
    }

    const body = el('div', 'product-card__body');
    body.appendChild(el('span', 'product-card__form', formLabel(formOf(p))));
    body.appendChild(el('h2', 'product-card__name', p.name));
    if (p.description) {
      body.appendChild(el('p', 'product-card__claim', withClaimMarker(p.description)));
    }
    const foot = el('div', 'product-card__foot');
    const price = el('span', 'price', formatPrice(p.priceCents, p.currency));
    foot.append(price, el('span', 'buy-link', p.inStock ? 'View' : 'See details'));
    body.appendChild(foot);
    if (!p.inStock) {
      body.appendChild(el('span', 'product-card__badge', 'Out of stock'));
    }

    card.append(media, body);
    return card;
  }
}
